import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import useDictationRecognition from './useDictationRecognition';
import useTriggerRecognition from './useTriggerRecognition';
import './App.css';

function App() {
  const [input, setInput] = useState('');
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;

  useEffect(() => {
    window.speechSynthesis.cancel();
  }, []);

  const {
    isRecording,
    startRecognition
  } = useDictationRecognition(
    async (transcript) => {
      setInput(transcript);
      await submitQuery(transcript);
    }
  );

  const handleStartDictation = () => {
    if (!isRecording && !isSpeaking) {
      startRecognition();
    }
  };

  const submitQuery = async (query) => {
    try {
      setIsLoading(true);
      const res = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: 'gpt-3.5-turbo',
          messages: [{ role: 'user', content: query }],
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
          },
        }
      );
      setResponse(res.data.choices[0]?.message?.content || '');
    } catch (err) {
      setResponse('Error: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = async (e) => {
    if (e.key === 'Enter' && input.trim()) {
      await submitQuery(input);
    }
  };

  const {
    read,
    pause,
    isSpeaking,
    isLoadingSpeech,
    isPaused,
    canPause
  } = useTriggerRecognition(response);

  const micIcon = isRecording
    ? 'bi-mic-fill text-danger flashing'  // 🔴 Flashing red mic when listening
    : 'bi-mic-mute-fill text-dark';  // ⚫ Black muted mic when inactive

  return (
    <div className="container mt-5">
      <h2 className="mb-4 text-center">Young Einstein</h2>

      <div className="input-group mb-3">
        <span
          className="input-group-text bg-white"
          style={{
            cursor: isSpeaking ? 'default' : 'pointer',
            opacity: isSpeaking ? 0.5 : 1,
          }}
          onClick={isSpeaking ? null : handleStartDictation}
          aria-disabled={isSpeaking}
        >
          <i className={`bi ${micIcon}`}></i>
        </span>
        <input
          type="text"
          className="form-control"
          placeholder="Ask something..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyPress}
          disabled={isSpeaking}
          style={{ opacity: isSpeaking ? 0.5 : 1 }}
          aria-disabled={isSpeaking}
        />
      </div>

      {isLoading && (
        <div className="text-center my-3 fade-in">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {!isLoading && response.trim() && (
        <>
          <div className="d-flex flex-wrap gap-2 justify-content-center my-3">
            <button
              className={`btn border-0 ${isLoadingSpeech ? "flashing" : ""}`}
              onClick={read}
              disabled={isLoadingSpeech || (isSpeaking && !isPaused)}
            >
              <i className="bi bi-play-fill"></i>
            </button>

            <button
              className="btn border-0"
              onClick={pause}
              disabled={!canPause}
            >
              <i className="bi bi-stop-fill"></i>
            </button>
          </div>

          <div className="border p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
            {response}
          </div>
        </>
      )}
    </div>
  );
}

export default App;
